import React, {useEffect, useState} from 'react';
import MaterialTableModule from 'material-table';
const MaterialTable = MaterialTableModule.default || MaterialTableModule;
import {tableIcons} from '../../util/icons';
import { TextField } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import {useDispatch, useSelector} from 'react-redux';
import { usePoolJurors, useCase } from '../../db/hooks';
import ScoreBadges from '../../components/Demographics/ScoreBadges';
import { deleteJuror, dismissJuror, restoreJurorToBox, updateJuror, buildRestoreToPoolAlert } from '../../db/actions';
import { getJurorDemographics } from '../../state/selectors';
import {ActionCreators as MultiSelectActionCreators} from "../../state/multiSelect/actions";
import MultiSelectModes from "../../state/multiSelect/modes";
import * as MultiSelectSelectors from "../../state/multiSelect/selectors";
import Display from '../../components/RichTextEditor/Display';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import StatusMenu from './StatusMenu';
import appTheme from '../../components/theme';
import sort from '../../util/sort';
import jsonifyCompare from '../../util/jsonifyCompare';
import {ActionCreators as EtherealActionCreators} from '../../state/ethereal/actions';
import {getActiveDrag, getJuryPoolBounds, getMode} from '../../state/ethereal/selectors';
import EmojiIndex from "./EmojiIndex";
import DismissMenu from "./DismissMenu";
import {mapDismissReason} from "./constants";
import JuryPoolDropSection from "./JuryPoolDropSection";
import JurorEditingBadge from '../../collaboration/JurorEditingBadge';
import {
    DemographicsFilterSelect,
    FilterBar,
    FilterSearchField,
    ScoreFilterControl,
    jurorMatchesDemographics,
    jurorMatchesScoreFilter,
    jurorMatchesText,
    useDebouncedValue,
    useSharedJurorFilters,
} from './JurorFilterControls';

const theme = createTheme({
  palette: { ...appTheme.palette },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#f5f5f5',
          padding: '0 16px 16px 16px',
        },
        elevation2: {
          boxShadow: '0 0 0 0',
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          backgroundColor: 'white',
          borderRadius: '4px 4px 0 0',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          padding: '6px',
          marginRight: '4px',
          marginLeft: '4px',
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        gutters: {
          paddingLeft: '8px',
          '@media (min-width: 600px)': {
            paddingLeft: '8px',
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h6: {
          fontWeight: 'bolder !important',
          padding: 0,
        },
      },
    },
  },
});

const useStyles = makeStyles((theme) => ({
    demographics: {
        marginRight: theme.spacing(1),
        alignItems: "center"
    },
    demographicsText: {
        fontSize: 14,
        color: theme.palette.text.hint
    },
    emojiIndex: {
        position: "absolute",
        top: -7,
        right: 0,
        backgroundColor: "white",
        padding: "3px 5px 2px 5px",
        borderRadius: "4px",
        fontSize: 12,
        zIndex: 100,
        boxShadow: "2px 2px 5px -2px black",
        overflowX: "hidden",
        maxWidth: "250px"
    }
}));

const columns = (onJurorUpdate, multiSelect, demographicCategories, showDemographics, classes, sorting, onOpenEdit, scoreDefinitions, scoringRules) => ([
    {
        width: 180,
        title: 'Juror ID / Name',
        field: 'nameAndId',
        render: (rowData) => (
            <Box display="flex" alignItems="center" gap={0.5}>
                <span>{rowData.nameAndId}</span>
                <JurorEditingBadge jurorId={rowData._id} size={18} />
            </Box>
        ),
        editComponent: props => (
            <TextField
                size="small"
                variant="outlined"
                fullWidth
                id="nameAndId"
                name="nameAndId"
                label="Juror ID / Name"
                value={props.value}
                onChange={e => props.onChange(e.target.value)}
            />
        ),
        defaultSort: sorting && sorting[0] ? sorting[0] : undefined,
        customSort: (a, b) => sort(a.nameAndId, b.nameAndId)
    },
    {
        title: 'Comments',
        field: 'comments',
        render: (rowData) => {

            let demographicsText = "";
            if (rowData.demographics) {
                demographicsText = getJurorDemographics(rowData, demographicCategories).map(demographic => demographic.value).join(", ");
            }

            return (
                <Box display="flex" flexDirection="row" justifyContent="space-between" gap={"1em"}>
                    <Box display="flex" flexDirection="row" alignItems="center" position="relative" flex={1}>
                        {showDemographics && !!demographicsText &&
                            <Box className={classes.demographics}>
                                <Typography variant="body2"
                                            className={classes.demographicsText}>{demographicsText}</Typography>
                            </Box>
                        }
                        <ScoreBadges scoreDefinitions={scoreDefinitions} scoringRules={scoringRules} juror={rowData} />
                        <Display richTextStyles={rowData.commentsStyles || []}
                                 text={rowData.comments || ""}
                                 maxLines={1}
                                 maxCharacters={125}/>
                        <EmojiIndex text={rowData.comments || ""}
                                    onClick={(emoji, occurrence) => {
                                        onOpenEdit(rowData, emoji, occurrence)
                                    }}
                                    className={classes.emojiIndex}/>
                    </Box>
                    <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-around" gap={"0.5em"} flex={0}>
                        {multiSelect.mode === MultiSelectModes.OFF &&
                            <StatusMenu key={`jury-pool-${rowData._id}`}
                                        updateStatus={(status) => {
                                            const newJurorInfo = {...rowData, status: status};
                                            onJurorUpdate(rowData._id, newJurorInfo);
                                        }}
                                        status={rowData.status ? rowData.status : null}
                                        className="icon-lg"
                            />
                        }

                    </Box>
                </Box>
            );
        },
        editComponent: props => (
            <TextField
                size="small"
                variant="outlined"
                fullWidth
                id="comments"
                name="comments"
                label="Comments"
                value={props.value}
                onChange={e => props.onChange(e.target.value)}
            />
        ),
        defaultSort: sorting && sorting[1] ? sorting[1] : undefined,
        customFilterAndSearch: (term, rowData) => {
            if (rowData.comments?.indexOf(term) >= 0) {
                return true;
            }
            if (rowData.nameAndId?.indexOf(term) >= 0) {
                return true;
            }
            if (rowData.demographics) {
                for (let oneDemographic of rowData.demographics) {
                    if (oneDemographic.value?.indexOf) {
                        if (oneDemographic.value.indexOf(term) >= 0) {
                            return true;
                        }
                    }
                }
            }
        }
    },
]);

const JuryPool = ({caseId, onOpenEdit, civilCase, demographicCategories, showDemographics}) => {
    const classes = useStyles();

    //console.log("Rendering JuryPool...");
    const dispatch = useDispatch();

    const activeDrag = useSelector(getActiveDrag);

    const poolBounds = useSelector(getJuryPoolBounds);
    const mode = useSelector(getMode);

    const modeLabel = mode === "jury-box" ? "Jury Box" : "Alternates";

    let poolRef = null;

    const serializeRect = (rect) => ({
        x: rect.x, y: rect.y, width: rect.width, height: rect.height,
        top: rect.top, right: rect.right, bottom: rect.bottom, left: rect.left
    });

    const updateBounds = (ref) => {
        if (ref) {
            poolRef = ref;
            const newBounds = serializeRect(ref.getBoundingClientRect());
            if (!jsonifyCompare('pool bounds')(poolBounds, newBounds)) {
                dispatch(EtherealActionCreators.setJuryPoolBounds(newBounds));
            }
        }
    };

    // Use Dexie hook for pool jurors
    const { poolJurors: jurors } = usePoolJurors(caseId);
    const { caseData } = useCase(caseId);
    const scoreDefinitions = caseData?.scoreDefinitions;
    const scoringRules = caseData?.scoringRules;
    
    const jurorsErrors = {}; // Errors now handled differently
    const multiSelect = useSelector(MultiSelectSelectors.getAll, jsonifyCompare('multiSelect'));
    const [pageSize, setPageSize] = useState(50);
    const [page, setPage] = useState(0);
    const [sorting, setSorting] = useState({});
    // Search + demographics are shared with the jury box and dismissed area.
    const {
        search: searchInput,
        setSearch: setSearchInput,
        demographics: demographicFilter,
        setDemographics: setDemographicFilter,
        scoreFilter,
        setScoreFilter,
    } = useSharedJurorFilters();
    const searchTerm = useDebouncedValue(searchInput.trim(), 150);

    const [width, setWidth] = useState(window.innerWidth);
    const updateWidth = () => {
        setWidth(window.innerWidth);
        if (poolRef) {
            dispatch(EtherealActionCreators.setJuryPoolBounds(serializeRect(poolRef.getBoundingClientRect())));
        }
    };

    useEffect(() => {
        window.addEventListener('resize', updateWidth);
        window.addEventListener('scroll', updateWidth);
        return () => {
            window.removeEventListener('resize', updateWidth);
            window.removeEventListener('scroll', updateWidth);
        }
    }, [width]);

    const jurorsWithErrors = jurors.filter(
        (juror) =>
            jurorMatchesText(juror, searchTerm) &&
            jurorMatchesDemographics(juror, demographicFilter) &&
            jurorMatchesScoreFilter(caseData, juror, scoreFilter)
    ).map((juror) => {
        return {
            ...juror,
            error: jurorsErrors[juror._id] || undefined,
        };
    }).sort((jurorA, jurorB) => {
        if (jurorA.created && jurorB.created) {
            return jurorB.created.localeCompare(jurorA.created) * -1; // ASC
        } else if (jurorA.created) {
            return 1;
        } else if (jurorB.created) {
            return -1;
        }
        return jurorA._id.localeCompare(jurorB._id);
    });

    const handleDeleteJuror = async (oldJurorData) => {
        await deleteJuror(caseId, oldJurorData._id);
    };

    const handleEditJuror = async (jurorId, newData) => {
        await updateJuror(caseId, jurorId, newData);
    }

    let jurorsInPage = pageSize,
        jurorsLeftOver = jurors.length - (page * pageSize);
    if (jurors.length < pageSize) {
        jurorsInPage = jurors.length;
    }
    if (jurorsLeftOver < pageSize) {
        jurorsInPage = jurorsLeftOver;
    }

    let minHeight = (jurorsInPage * 39) + 50 + 70;

    if (activeDrag) {
        return (
            <JuryPoolDropSection key="juryPoolDropSection" caseId={caseId} />
        );
    }

    return (
        <StyledEngineProvider injectFirst>
            {/* Rendered outside the pool's ThemeProvider: its MuiPaper override
                (grey background + padding) would deform the Autocomplete popup.
                MaterialTable's built-in search is disabled in favor of this
                shared bar so all three areas have uniform filter controls. */}
            <FilterBar style={{justifyContent: 'flex-end', padding: '0 16px', marginBottom: 4}}>
                <FilterSearchField
                    value={searchInput}
                    onChange={setSearchInput}
                    placeholder="Search pool…"
                />
                <DemographicsFilterSelect
                    jurors={jurors}
                    value={demographicFilter}
                    onChange={setDemographicFilter}
                    placeholder="Demographics"
                    minWidth={180}
                />
                <ScoreFilterControl
                    scoreDefinitions={scoreDefinitions || []}
                    value={scoreFilter}
                    onChange={setScoreFilter}
                />
            </FilterBar>
            <ThemeProvider theme={theme} key="juryPoolRendered">
                <div ref={updateBounds} style={{minHeight}}>
                    <MaterialTable
                        title="Jury Pool"
                        columns={columns(handleEditJuror, multiSelect, demographicCategories, showDemographics, classes, sorting, onOpenEdit, scoreDefinitions, scoringRules)}
                        data={jurorsWithErrors}
                        onChangePage={(newPage) => {
                            //console.log("New page: ", newPage);
                            setPage(newPage);
                        }}
                        onChangeRowsPerPage={(newPageSize) => {
                            setPageSize(newPageSize);
                        }}
                        onOrderChange={(orderedColumnId, orderDirection) => {
                            setSorting({ [orderedColumnId]: orderDirection });
                        }}
                        options={{
                            showTitle: true,
                            search: false,
                            paging: true,
                            pageSize: pageSize,
                            pageSizeOptions: [10, 25, 50, 75],
                            emptyRowsWhenPaging: false,
                            sorting: true,
                            headerStyle: {
                                fontWeight: 'bold',
                                fontSize: 16,
                                padding: '8px',
                            },
                            cellStyle: {padding: '8px'},
                            actionsColumnIndex: -1,
                        }}
                        icons={tableIcons}
                        editable={{
                            isDeleteHidden: function () {
                                return multiSelect.mode !== MultiSelectModes.OFF
                            },
                            onRowDelete: (oldData) => {
                                //console.log('Attempting to delete juror: ', oldData);
                                return handleDeleteJuror(oldData);
                            },
                        }}
                        actions={[
                            (rowData) => ({
                                icon: tableIcons.MoveToJuryBox,
                                hidden: multiSelect.mode !== MultiSelectModes.OFF,
                                tooltip: `Move to ${modeLabel}`,
                                onClick: async (event, rowData) => {
                                    console.log(`Moving to ${modeLabel}: `, {
                                        jurorId: rowData._id,
                                        mode
                                    });
                                    const { placedInPool } = await restoreJurorToBox(caseId, rowData._id, mode);
                                    if (placedInPool) {
                                        const alert = buildRestoreToPoolAlert(mode);
                                        dispatch(EtherealActionCreators.addAlert(alert.message, alert.title, alert.variant));
                                    }
                                },
                            }),
                            (rowData) => ({
                                hidden: multiSelect.mode !== MultiSelectModes.OFF,
                                icon: () => (
                                    <DismissMenu onSelected={async (reason) => {
                                        await dismissJuror(caseId, rowData._id, { ...mapDismissReason(reason), mode });
                                    }}/>
                                )
                            }),
                            {
                                icon: tableIcons.Add,
                                hidden: multiSelect.mode !== MultiSelectModes.OFF,
                                tooltip: 'Add juror',
                                isFreeAction: true,
                                onClick: (e) => {
                                    e.preventDefault();
                                    onOpenEdit('new');
                                },
                            },
                            (rowData) => ({
                                icon: tableIcons.Error,
                                hidden: rowData.error === undefined || multiSelect.mode !== MultiSelectModes.OFF,
                                tooltip: rowData.error && rowData.error.error ? rowData.error.error.toString() : '',
                                onClick: (event, rowData) => {
                                    dispatch(ActionCreators.removeJurorError(rowData._id));
                                },
                            }),
                            (rowData) => {
                                return {
                                    hidden: multiSelect.mode === MultiSelectModes.OFF,
                                    icon: (multiSelect.selectedJurorIds.indexOf(rowData._id) >= 0) ? tableIcons.Selected : tableIcons.NotSelected
                                }
                            }
                        ]}
                        onRowClick={(event, rowData) => {

                            if (event.target?.closest(".dropdown-menu")) {
                                return true;
                            }

                            event.preventDefault();

                            if (multiSelect.mode === MultiSelectModes.ON) {
                                if (multiSelect.selectedJurorIds.indexOf(rowData._id) >= 0) {
                                    dispatch(MultiSelectActionCreators.removeJurorId(rowData._id));
                                } else {
                                    dispatch(MultiSelectActionCreators.addJurorId(rowData._id));
                                }
                            } else {
                                onOpenEdit(rowData);
                            }
                        }}
                        localization={{
                            body: {
                                emptyDataSourceMessage: (demographicFilter.length > 0 || searchTerm.length > 0 || !!scoreFilter?.scoreDefinitionId)
                                    ? <b>No jurors in the pool match the current filters</b>
                                    : <b>Click plus button to add jurors to pool</b>,
                            },
                        }}
                    />
                </div>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};

export default React.memo(JuryPool);
